import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Sparkle, PaperPlaneTilt, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MainsWrite() {
    const { promptId } = useParams();
    const [prompt, setPrompt] = useState(null);
    const [text, setText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            const { data } = await api.get(`/mains/prompts/${promptId}`);
            setPrompt(data);
        })();
    }, [promptId]);

    const words = text.trim().split(/\s+/).filter(Boolean).length;

    const submit = async () => {
        if (words < 20) return toast.error("Please write at least 20 words.");
        setSubmitting(true);
        toast.info("Sending your answer to the evaluator…");
        try {
            const { data } = await api.post("/mains/submit", { prompt_id: promptId, answer_text: text });
            toast.success("Evaluated!");
            navigate(`/mains/${data.submission_id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Submit failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (!prompt) return <Layout><div className="text-slate-500">Loading…</div></Layout>;

    return (
        <Layout>
            <button onClick={() => navigate("/mains")} className="btn-ghost mb-4"><ArrowLeft size={16} /> Back</button>
            <div className="grid lg:grid-cols-12 gap-6">
                <aside className="lg:col-span-4 space-y-4">
                    <div className="bento-card sticky top-24">
                        <div className="text-[11px] uppercase tracking-widest text-[#1E3A8A]">{prompt.subject} · {prompt.marks} marks</div>
                        <div className="font-heading font-bold text-xl mt-2 leading-snug">Prompt</div>
                        <div className="mt-3 text-slate-800 text-[15px] leading-relaxed">{prompt.prompt}</div>
                        <div className="mt-6 p-3 rounded-lg bg-slate-50 border border-[#BFD3FF] text-xs text-slate-500">
                            <div className="flex items-center gap-1.5 text-[#1E3A8A] mb-1"><Sparkle size={14} weight="fill" /> Tips</div>
                            <ul className="space-y-1 list-disc list-inside">
                                <li>Structure: Introduction → Body → Conclusion.</li>
                                <li>Use Karnataka-specific examples where possible.</li>
                                <li>Balance analysis with facts and dates.</li>
                                <li>Keep language crisp; avoid filler phrases.</li>
                            </ul>
                        </div>
                    </div>
                </aside>
                <div className="lg:col-span-8">
                    <div className="bento-card">
                        <div className="flex justify-between items-center mb-3">
                            <div className="font-heading font-semibold">Your Answer</div>
                            <div className="text-xs text-slate-500 font-mono">
                                <span className={words < 100 ? "text-red-400" : words > 350 ? "text-[#1E3A8A]" : "text-emerald-400"}>{words}</span> words
                            </div>
                        </div>
                        <textarea
                            data-testid="mains-textarea"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={18}
                            placeholder="Begin with a crisp introduction that frames the question…"
                            className="w-full bg-white border border-[#BFD3FF] rounded-xl p-4 text-slate-900 font-sans text-[15px] leading-relaxed focus:outline-none focus:border-[#1E3A8A]/60 focus:ring-2 focus:ring-gold/20 resize-y"
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <button
                                data-testid="submit-mains-btn"
                                onClick={submit}
                                disabled={submitting || !text.trim()}
                                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitting ? "Evaluating…" : "Submit for Evaluation"} <PaperPlaneTilt size={16} weight="fill" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
